/**
 * Last-touch attribution capture for purchase events.
 *
 * Survives SPA navigation by snapshotting UTMs/click IDs into sessionStorage
 * the moment a marketing-tagged URL is seen. Read at click time and forwarded
 * through the checkout edge functions → Stripe metadata → webhook → DB row.
 *
 * Last-touch semantics: every new tagged URL overwrites the previous snapshot.
 * Dies with the tab (sessionStorage), unlike first-touch attribution.ts which
 * lives in localStorage.
 */
const STORAGE_KEY = "nimi_last_touch_utms_v1";

const TRIGGER_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
] as const;

type TriggerKey = (typeof TRIGGER_KEYS)[number];

export type LastTouchAttribution = Partial<Record<TriggerKey, string>>;

export type PurchaseAttributionBundle = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  fbp?: string;
  fbc?: string;
};

const isBrowser = () => typeof window !== "undefined";

const readSession = (): LastTouchAttribution | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LastTouchAttribution) : null;
  } catch {
    return null;
  }
};

const writeSession = (value: LastTouchAttribution) => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore (private mode, quota, etc.)
  }
};

const readUrlParams = (): LastTouchAttribution => {
  if (!isBrowser()) return {};
  const params = new URLSearchParams(window.location.search);
  const out: LastTouchAttribution = {};
  for (const key of TRIGGER_KEYS) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  return out;
};

const readCookie = (name: string): string | undefined => {
  if (!isBrowser()) return undefined;
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]+)")
  );
  return match ? decodeURIComponent(match[1]) : undefined;
};

/**
 * Read _fbp / _fbc cookies set by the Meta Pixel.
 * Live read — these may rotate, so always read at click time, never persist.
 */
export const getFbCookies = (): { fbp?: string; fbc?: string } => {
  return {
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc"),
  };
};

/**
 * If the current URL has any UTM/click-ID param, snapshot it to sessionStorage.
 * Call on every route change. No-op if URL has no marketing params.
 *
 * Trigger params match captureFirstTouchAttribution() in src/lib/attribution.ts:
 * any of utm_source/medium/campaign/content/term, gclid, or fbclid.
 */
export const captureLastTouchFromUrl = (): LastTouchAttribution | null => {
  if (!isBrowser()) return null;
  const fromUrl = readUrlParams();
  const hasAny = TRIGGER_KEYS.some((k) => fromUrl[k]);
  if (!hasAny) return null;
  writeSession(fromUrl);
  return fromUrl;
};

/**
 * Read last-touch attribution. Falls back to current URL params if sessionStorage
 * is empty (covers single-page direct-buy with no prior route change).
 */
export const getLastTouchAttribution = (): LastTouchAttribution => {
  const stored = readSession();
  if (stored && Object.keys(stored).length > 0) return stored;
  return readUrlParams();
};

/**
 * Convenience: build the full bundle (UTMs + click IDs + Meta cookies) ready
 * to spread into a supabase.functions.invoke({ body }) call.
 */
export const getPurchaseAttributionBundle = (): PurchaseAttributionBundle => {
  const attr = getLastTouchAttribution();
  const { fbp, fbc } = getFbCookies();
  const bundle: PurchaseAttributionBundle = {};
  if (attr.utm_source) bundle.utm_source = attr.utm_source;
  if (attr.utm_medium) bundle.utm_medium = attr.utm_medium;
  if (attr.utm_campaign) bundle.utm_campaign = attr.utm_campaign;
  if (attr.utm_content) bundle.utm_content = attr.utm_content;
  if (attr.utm_term) bundle.utm_term = attr.utm_term;
  if (attr.gclid) bundle.gclid = attr.gclid;
  if (attr.fbclid) bundle.fbclid = attr.fbclid;
  if (fbp) bundle.fbp = fbp;
  if (fbc) bundle.fbc = fbc;
  return bundle;
};

/**
 * Build the argument bundle for credit/unlimited booking RPCs
 * (`book_event_by_credits`, `book_event_unlimited`).
 *
 * Mirrors getPurchaseAttributionBundle() but renames keys to match the RPC
 * signature (`_purchase_*`) and includes navigator.userAgent so the resulting
 * ticket row reaches schema parity with Stripe-paid tickets — required for
 * future server-side CAPI Purchase events on £0 bookings.
 */
export const getCreditBookingAttributionArgs = () => {
  const a = getLastTouchAttribution();
  const { fbp, fbc } = getFbCookies();
  return {
    _purchase_utm_source: a.utm_source ?? null,
    _purchase_utm_medium: a.utm_medium ?? null,
    _purchase_utm_campaign: a.utm_campaign ?? null,
    _purchase_utm_content: a.utm_content ?? null,
    _purchase_utm_term: a.utm_term ?? null,
    _purchase_gclid: a.gclid ?? null,
    _purchase_fbclid: a.fbclid ?? null,
    _purchase_fbp: fbp ?? null,
    _purchase_fbc: fbc ?? null,
    _purchase_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  };
};
